import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { extractErrorCode, friendlyAuthError } from '@/lib/auth-errors';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface SignInFormProps {
  /** Called after a successful sign-in so the host modal can close. */
  onSuccess?: () => void;
}

/**
 * Email + password sign-in. ALREADY_LOGGED_IN gets a special inline error
 * panel because it's the most likely failure mode in practice — and
 * because telling the user "you're already logged in elsewhere" is
 * meaningfully different from "wrong password".
 */
export function SignInForm({ onSuccess }: SignInFormProps) {
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorCode(null);
    setErrorText(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      onSuccess?.();
    } catch (err) {
      setErrorCode(extractErrorCode(err));
      setErrorText(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-5">
      <div className="grid gap-2">
        <Label htmlFor="signin-email">Email</Label>
        <Input
          id="signin-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={submitting}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="signin-password">Password</Label>
        <Input
          id="signin-password"
          type="password"
          autoComplete="current-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          disabled={submitting}
        />
      </div>

      {errorCode === 'ALREADY_LOGGED_IN' ? (
        <div
          role="alert"
          className="rounded-sm border border-vermilion/40 bg-vermilion/10 p-3"
        >
          <p className="text-sm font-medium text-ink">
            You are already signed in on another device.
          </p>
          <p className="mt-1 text-xs text-graphite leading-relaxed">
            AURA enforces one active session per account. Sign out from the
            other browser or device first, then try again here.
          </p>
        </div>
      ) : errorText ? (
        <p role="alert" className="text-sm text-vermilion">
          {errorText}
        </p>
      ) : null}

      <Button type="submit" disabled={submitting} className="w-full">
        {submitting ? <LoadingSpinner label="Signing in…" size={16} /> : 'Sign in'}
      </Button>
    </form>
  );
}
